import Comparer from "../../app/comparer";
import { useState, useEffect, useMemo, createContext } from "react";
import { useParams } from "react-router-dom";
import { useMsal, } from '@azure/msal-react';
import UserApi from '../../api/user//user-api-factory';
import GraphApi from './../../api/graph/graph-api';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import { UserDetailsTab } from '../../components/user/user-details-tab';
import { UserActivityTab } from '../../components/user/user-activity-tab';
import { UserProfileImages } from '../../components/user/user-profile-images';
import useTrackedProperty from "../../app/useTrackedProperty";

import 'react-image-crop/dist/ReactCrop.css';
import './user-settings.css';
import { arrayAny } from "../../utils/arrayUtils";

export const UserContext = createContext();

export const UserSettings = () => {

  const { id } = useParams();
  const { instance, inProgress, accounts } = useMsal();

  const self = id === "me" || arrayAny(accounts.filter(a => a.idTokenClaims.oid === id));

  const comparer = new Comparer();

  const graphApi = useMemo(() => new GraphApi(instance, inProgress, accounts, false), [instance, inProgress, accounts]);
  const userApi = useMemo(() => new UserApi(instance, inProgress, accounts, false), [instance, inProgress, accounts]);

  const [myProfile, setMyProfile] = useState(null);
  const [myOriginalProfile, setMyOriginalProfile] = useState(null);
  const [myGraphProfile, setMyGraphProfile] = useState({ id: null, routeId: null });
  const [myAAPhotoUrl, setMyAAPhotoUrl] = useState(null);
  const [fetchingProfileImage, setFetchingProfileImage] = useState(false);
  const [fetchProfileImageComplete, setFetchProfileImageComplete] = useState(false);
  const [rerender, setRerender] = useState(false);
  const [validationErrors, setValidationErrors] = useState(null);
  const [fetchingUserData, setFetchingUserData] = useState(false);
  const [fetchUserDataComplete, setFetchUserDataComplete] = useState(false);

  const myGraphProfileRouteId = useTrackedProperty(myGraphProfile, "routeId");

  useEffect(() => {

    if ((graphApi && userApi) && ((!fetchingUserData && !fetchUserDataComplete) || (!fetchingUserData && myGraphProfileRouteId !== id))) {
      setFetchUserDataComplete(false);
      setFetchingUserData(true);

      const loadUserAsync = async () => {
        try {
          const graphUser = self ? await graphApi.getMeDetails() : await graphApi.getUser(id);

          // Preserve Graph's own id; track the current route id separately
          const routeId = id;
          setMyGraphProfile({ ...graphUser, id: graphUser.id, routeId });

          // For self, always use Entra Object Id (GUID) for backend lookup
          const backendUserId = self
            ? (graphUser.objectId || graphUser.id)
            : id;

          const systemUsr = await userApi.user(backendUserId);
          setMyProfile(systemUsr);
          setMyOriginalProfile(structuredClone(systemUsr));
        } finally {
          setFetchingUserData(false);
          setFetchUserDataComplete(true);
        }
      };

      loadUserAsync();

      if (!fetchingProfileImage && !myAAPhotoUrl && !fetchProfileImageComplete) {
        setFetchingProfileImage(true)

        graphApi.fetchProfilePhoto().then(data => {
          setMyAAPhotoUrl(data);
        })
          .catch(err => console.error("Failed to load image", err))
          .finally(() => { setFetchingProfileImage(false); setFetchProfileImageComplete(true); });
      }
    }
  }, [self, graphApi, userApi, accounts, fetchingProfileImage, myAAPhotoUrl, fetchProfileImageComplete, fetchingUserData, fetchUserDataComplete, id, myGraphProfileRouteId]);


  const hasChanges = () => {
    const equal = comparer.equals(myProfile, myOriginalProfile);
    return !equal;
  };

  const canSave = () => {
    let result = hasChanges();
    result &&= validationErrors ? Object.keys(validationErrors).length === 0 : result;
    return result;
  };

  const handleCancel = () => {
    setMyProfile(structuredClone(myOriginalProfile));
    setValidationErrors(null);
    setRerender(!rerender);
  };

  const handleSave = () => {
    console.log("Save", myProfile);

    userApi.updateUser(myProfile)
      .then(data => {
        console.log("User updated", data);
        setMyProfile(data);
        setMyOriginalProfile(structuredClone(data));
      })
      .catch(err => console.error("An error occurred updating User Profile.", err));
  };

  const handleSetMyProfile = (newProfile) => {
    setMyProfile(newProfile);
  }

  return (
    <UserContext.Provider value={{ myProfile, setMyProfile: handleSetMyProfile, setValidationErrors, self, graphApi, userApi }}>
      <Tabs>
        <TabList className="nav nav-pills mb-2" >
          <Tab className="nav-item me-2" > <div className="nav-link active">User Details</div></Tab>
          {!!self && <Tab disabled={!!!self} className="nav-item me-2" > <div className="nav-link active">Profile Images</div></Tab>}
          <Tab className="nav-item me-2" > <div className="nav-link active">Activity</div></Tab>

        </TabList>

        <TabPanel>
          <div className="tab-content panel p-3 rounded">
            <div className="tab-pane fade active show" id="default-tab-1">
              <UserDetailsTab user={myProfile} />
            </div>
          </div>
        </TabPanel>

        {!!self && <TabPanel>
          <div className="tab-content panel p-3 rounded">
            <div className="tab-pane fade active show" id="default-tab-2">
              <UserProfileImages aadPhotoDataUrl={myAAPhotoUrl} graphProfile={myGraphProfile} systemProfile={myProfile} setSystemProfile={setMyProfile} setOriginalSystemProfile={setMyOriginalProfile} />
            </div>
          </div>
        </TabPanel>}

        <TabPanel>
          <div className="tab-content panel p-3 rounded">
            <div className="tab-pane fade active show" id="default-tab-3">
              <UserActivityTab />
            </div>
          </div>
        </TabPanel>

      </Tabs>
      <div className="row">
        <div className="col-xl-12">
          {canSave() && <button className="btn btn-primary" disabled={!canSave()} onClick={() => handleSave()}>Save</button>}
          {hasChanges() && <button className="btn btn-secondary" disabled={!hasChanges()} onClick={() => handleCancel()}>Cancel</button>}
        </div>
      </div>
    </UserContext.Provider>
  )
};